/*
 * Photo annotation geometry: pure functions, no React, no canvas.
 *
 * Coordinates are fractions of the photograph (0..1 on each axis), so an outline
 * survives thumbnails, zoom and re-encoding. Limits mirror
 * mushroom-bank/lambda/routes/photo_annotations.py; the backend test reads the
 * numbers below and fails if the two drift.
 */
export const LAYERS = ['cap', 'stipe', 'fertile_layer', 'artifact'];
export const FERTILE_TYPES = ['gills', 'pores', 'teeth', 'other'];
// Ignore / Artifact (migration 0027): hands, rulers, coins, keys. It belongs to the
// photograph, so its entry is specimen 0, and every painting shape names what it is.
export const ARTIFACT_LAYER = 'artifact';
export const ARTIFACT_SPECIMEN = 0;
export const ARTIFACT_LABELS = ['hand', 'ruler', 'coin', 'keys', 'other'];
// Okabe-Ito: distinguishable with every common colour-vision deficiency, and
// every layer is also named in the legend, never identified by colour alone.
// The artifact layer is neutral gray with dark diagonal stripes: the pattern, not
// the colour, sets it apart from the mushroom parts.
export const LAYER_COLOURS = { cap: '#E69F00', stipe: '#0072B2', fertile_layer: '#009E73', artifact: '#9CA3AF' };
export const ARTIFACT_STRIPE = '#374151';
export const ARTIFACT_HATCH_CSS = `repeating-linear-gradient(45deg, ${LAYER_COLOURS.artifact} 0 3px, ${ARTIFACT_STRIPE} 3px 5px)`;

export const LIMITS = Object.freeze({
  maxSpecimen: 50,
  maxEntries: 150,
  maxShapes: 200,
  maxPoints: 4000,
  maxTotalPoints: 40000,
  maxStrokeWidth: 0.25,
});

export const clamp01 = v => Math.min(1, Math.max(0, v));
const round4 = v => Math.round(v * 10000) / 10000;

export function distance(a, b) {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

/** Drop points closer than `min` to the previous kept point. */
export function dedupe(points, min) {
  const out = [];
  points.forEach(p => {
    if (!out.length || distance(out[out.length - 1], p) >= min) out.push(p);
  });
  return out;
}

function perpendicular(p, a, b) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const length = Math.hypot(dx, dy);
  if (length === 0) return distance(p, a);
  return Math.abs(dy * p[0] - dx * p[1] + b[0] * a[1] - b[1] * a[0]) / length;
}

/** Douglas-Peucker, iterative (a long stroke must not overflow the stack), rounded to 4 decimals. */
export function simplifyPath(points, tolerance) {
  const rounded = points.map(([x, y]) => [round4(clamp01(x)), round4(clamp01(y))]);
  if (rounded.length <= 2 || tolerance <= 0) return dedupe(rounded, 0.0001);
  const keep = new Array(rounded.length).fill(false);
  keep[0] = true;
  keep[rounded.length - 1] = true;
  const stack = [[0, rounded.length - 1]];
  while (stack.length) {
    const [first, last] = stack.pop();
    let index = -1;
    let max = 0;
    for (let i = first + 1; i < last; i += 1) {
      const d = perpendicular(rounded[i], rounded[first], rounded[last]);
      if (d > max) { max = d; index = i; }
    }
    if (index !== -1 && max > tolerance) {
      keep[index] = true;
      stack.push([first, index], [index, last]);
    }
  }
  return rounded.filter((_, i) => keep[i]);
}

export function pointInPolygon([x, y], polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** The painted extent of some shapes (erase strokes do not add paint), or null. */
export function boundingBox(shapes) {
  let box = null;
  (shapes || []).forEach(shape => {
    if (shape.erase) return;
    const pad = shape.kind === 'stroke' ? (shape.width || 0) / 2 : 0;
    shape.points.forEach(([x, y]) => {
      box = box || { minX: 1, minY: 1, maxX: 0, maxY: 0 };
      box.minX = Math.min(box.minX, clamp01(x - pad));
      box.minY = Math.min(box.minY, clamp01(y - pad));
      box.maxX = Math.max(box.maxX, clamp01(x + pad));
      box.maxY = Math.max(box.maxY, clamp01(y + pad));
    });
  });
  return box;
}

/** The numbered mushrooms; the artifact layer belongs to the photograph and is not one. */
export function specimenIndexes(entries) {
  return [...new Set((entries || []).filter(e => e.layer !== ARTIFACT_LAYER).map(e => e.specimen_index))]
    .sort((a, b) => a - b);
}

export function countShapes(entries) {
  return (entries || []).reduce((n, e) => n + (e.geometry?.shapes?.length || 0), 0);
}

const sameSlot = (entry, specimen, layer) => entry.specimen_index === specimen && entry.layer === layer;

/** A new entries array with `shape` added to (specimen, layer), creating that layer if needed.
 * An eraser stroke on a layer that holds no paint changes nothing. */
export function addShape(entries, { specimen, layer, fertileType }, shape) {
  const list = entries || [];
  const index = list.findIndex(e => sameSlot(e, specimen, layer));
  if (index === -1) {
    if (shape.erase) return list;
    return [...list, {
      specimen_index: specimen, layer,
      fertile_type: layer === 'fertile_layer' ? (fertileType || null) : null,
      geometry: { shapes: [shape] },
    }];
  }
  return list.map((e, i) => (i !== index ? e : {
    ...e,
    fertile_type: layer === 'fertile_layer' ? (fertileType || e.fertile_type || null) : null,
    geometry: { shapes: [...(e.geometry?.shapes || []), shape] },
  }));
}

export function removeLayer(entries, specimen, layer) {
  return (entries || []).filter(e => !sameSlot(e, specimen, layer));
}

/** The first problem the server would refuse, or null. */
export function validateEntries(entries) {
  if (!Array.isArray(entries)) return 'annotations must be a list';
  if (entries.length > LIMITS.maxEntries) return 'tooManyLayers';
  let total = 0;
  const seen = new Set();
  for (const entry of entries) {
    const key = `${entry.specimen_index}|${entry.layer}`;
    if (seen.has(key)) return 'duplicateLayer';
    seen.add(key);
    if (!LAYERS.includes(entry.layer)) return 'invalidLayer';
    const artifact = entry.layer === ARTIFACT_LAYER;
    if (artifact ? entry.specimen_index !== ARTIFACT_SPECIMEN
      : !(entry.specimen_index >= 1 && entry.specimen_index <= LIMITS.maxSpecimen)) {
      return 'invalidLayer';
    }
    const shapes = entry.geometry?.shapes || [];
    if (!shapes.length) return 'emptyLayer';
    if (shapes.length > LIMITS.maxShapes) return 'tooDetailed';
    for (const shape of shapes) {
      if (shape.points.length > LIMITS.maxPoints) return 'tooDetailed';
      if (shape.kind === 'polygon' && shape.points.length < 3) return 'invalidShape';
      const labelled = shape.label !== undefined && shape.label !== null;
      if (artifact ? (labelled ? !ARTIFACT_LABELS.includes(shape.label) : !shape.erase) : labelled) return 'invalidShape';
      total += shape.points.length;
    }
  }
  return total > LIMITS.maxTotalPoints ? 'tooDetailed' : null;
}
